import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { BrandEmblem } from "../components/portal/BrandEmblem";
import { Loader2, LogOut, ArrowLeft, Inbox, CalendarDays, MailCheck, Download, KeyRound, XCircle, CalendarClock, ScrollText, SlidersHorizontal, FileDown, FileText, BadgeCheck, Users, Briefcase, Trash2, LayoutDashboard, Map, FolderLock, ClipboardCheck, Activity, ChevronRight, ChevronLeft, ShieldCheck, History } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid } from "recharts";
import { FormSetup } from "../components/portal/FormSetup";
import { Modal } from "../components/portal/Modal";
import { Pathways } from "../components/portal/Pathways";
import { AutonomousSuites } from "../components/portal/AutonomousSuites";
import { Scheduler } from "../components/portal/Scheduler";
import { NursysEnrollPanel } from "../components/portal/NursysEnrollPanel";
import { ResourceVault } from "../components/portal/ResourceVault";
import { ComplianceAudit } from "../components/portal/ComplianceAudit";
import { ComplianceVerification, ResultCard } from "../components/portal/ComplianceVerification";
import { VacancyBoard } from "../components/portal/VacancyBoard";
import { IntakeModal } from "../components/portal/IntakeModal";
import { TRACKS } from "../data/content";

const ROLE_TRACKS = {
  university: ["universities", "lpn"],
  facility: ["facilities", "pediatric", "homecare"],
  preceptor: ["preceptors"],
  img: ["img"],
};

const downloadCsv = async (kind, filename) => {
  const res = await api.get(`/exports/${kind}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const MonitoringWidgets = () => {
  const [mon, setMon] = useState(null);
  useEffect(() => {
    api.get("/dashboard/monitoring").then(({ data }) => setMon(data)).catch(() => {});
  }, []);
  if (!mon) return null;
  return (
    <div className="mt-8" data-testid="monitoring-widgets">
      <div className="flex items-center gap-2.5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <h3 className="text-xs font-bold tracking-[0.18em] uppercase text-[#64748B]">Live Monitoring — Real-Time Telemetry</h3>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm" data-testid="monitoring-attendance">
          <div className="text-sm font-semibold text-[#0B1B2B]">Live Student Attendance</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">Weekly presence across active cohort</div>
          <div className="mt-4 h-44">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
              <BarChart data={mon.attendance} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="present" stackId="a" fill="#D4AF37" radius={[3, 3, 0, 0]} name="Present" />
                <Bar dataKey="absent" stackId="a" fill="#CBD5E1" radius={[3, 3, 0, 0]} name="Absent" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 shadow-sm" data-testid="monitoring-accruals">
          <div className="text-sm font-semibold text-[#0B1B2B]">Clinical Hour Accruals</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">Cumulative verified hours vs. target trajectory</div>
          <div className="mt-4 h-44">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
              <AreaChart data={mon.accruals}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }} />
                <Area type="monotone" dataKey="target" stroke="#CBD5E1" strokeDasharray="5 4" fill="none" name="Target" />
                <Area type="monotone" dataKey="accrued" stroke="#D4AF37" strokeWidth={2.5} fill="url(#goldFill)" name="Accrued" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-[#0B1B2B] rounded-xl p-5 shadow-sm flex flex-col" data-testid="monitoring-clearances">
          <div className="text-sm font-semibold text-white">Compliance Clearances</div>
          <div className="text-xs text-slate-400 mt-0.5">Credential & clearance posture</div>
          <div className="mt-5 font-serif text-4xl text-[#D4AF37]">{mon.clearances.rate}%</div>
          <div className="text-xs text-slate-400 mt-1">cohort fully cleared</div>
          <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-700" style={{ width: `${mon.clearances.rate}%` }} />
          </div>
          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            {[["Cleared", mon.clearances.cleared, "text-emerald-400"], ["Pending", mon.clearances.pending, "text-amber-400"], ["Expiring", mon.clearances.expiring, "text-red-400"]].map(([l, v, c]) => (
              <div key={l} className="bg-white/5 rounded-lg py-2.5">
                <div className={`font-serif text-xl ${c}`}>{v}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const BUCKET_LABELS = {
  universities: "Universities & Nursing Programs",
  facilities: "Healthcare Facilities & Health Systems",
  vocational: "Vocational & LPN Schools",
  preceptors: "Preceptors & Clinicians",
  global: "Global USCE / IMG Pathways",
};

const BucketEngagement = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/bucket-clicks").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return <p className="mt-8 text-sm text-[#64748B]" data-testid="engagement-loading">Loading engagement data…</p>;
  const chartData = stats.counts.map((c) => ({ name: BUCKET_LABELS[c.bucket] || c.bucket, clicks: c.count }));
  return (
    <div className="mt-8" data-testid="bucket-engagement">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-[#0B1B2B]">Partner Bucket Clicks</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">Which "Tell us who you are" hero buttons visitors click most</div>
          </div>
          <div className="text-right">
            <div className="font-serif text-3xl text-[#0B1B2B]" data-testid="engagement-total">{stats.total}</div>
            <div className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Total clicks</div>
          </div>
        </div>
        <div className="mt-6 h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={230} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="clicks" fill="#D4AF37" radius={[0, 4, 4, 0]} name="Clicks" barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const TOUR_STEPS = [
  { id: "console", title: "Operations Console", desc: "Your command center — live stats, real-time attendance, clinical hour accruals, and compliance clearance telemetry." },
  { id: "pathways", title: "Operational Pathways", desc: "Your provisioned track engines — deployment frameworks, specialty compliance standards, and direct institutional intake." },
  { id: "scheduler", title: "Executive Scheduler", desc: "Book confidential executive briefings directly against our live availability calendar." },
  { id: "vault", title: "Resource Vault", desc: "Executive-series whitepapers — regulatory mechanics, accreditation blueprints, and revenue alignment briefings." },
  { id: "assessment", title: "Self-Assessment", desc: "Diagnose operational bottlenecks in five clicks and generate a custom pod solution with a pre-filled intake." },
  { id: "placements", title: "Placements", desc: "Live pod placements and faculty engagements across the network — express interest in one click." },
];

const PortalTour = ({ step, onNext, onBack, onSkip }) => {
  const s = TOUR_STEPS[step];
  const last = step === TOUR_STEPS.length - 1;
  return (
    <div className="fixed bottom-6 right-6 z-50 w-[340px] bg-[#0B1B2B] border border-[#D4AF37]/50 rounded-2xl shadow-2xl p-6" data-testid="portal-tour">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#D4AF37]">Portal Tour · {step + 1} / {TOUR_STEPS.length}</span>
        <button onClick={onSkip} className="text-slate-400 hover:text-white text-xs" data-testid="portal-tour-skip">Skip</button>
      </div>
      <h4 className="mt-3 font-serif text-lg text-white" data-testid="portal-tour-title">{s.title}</h4>
      <p className="mt-1.5 text-xs text-slate-300 leading-relaxed">{s.desc}</p>
      <div className="mt-4 flex items-center gap-1.5">
        {TOUR_STEPS.map((_, i) => (
          <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-5 bg-[#D4AF37]" : "w-1.5 bg-white/25"}`} />
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2">
        {step > 0 && (
          <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-300 hover:text-white px-3 py-2 rounded-lg border border-white/15 transition-colors" data-testid="portal-tour-back">
            <ChevronLeft size={13} /> Back
          </button>
        )}
        <button onClick={onNext} className="flex-1 flex items-center justify-center gap-1 bg-[#D4AF37] text-[#0B1B2B] text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-[#e8c968] transition-colors" data-testid="portal-tour-next">
          {last ? "Finish Tour" : "Next"} {!last && <ChevronRight size={13} />}
        </button>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { onClose(); setDone(false); setCurrent(""); setNext(""); setError(""); }} testId="change-password-modal">
      <div className="p-8 md:p-10">
        <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">Account Security</p>
        <h2 className="mt-2 font-serif text-2xl text-[#0B1B2B] tracking-tight">Change Password</h2>
        {done ? (
          <p className="mt-6 text-sm text-emerald-700 font-medium" data-testid="change-password-success">
            Password updated. Use your new password from your next sign-in.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input className={`${inputCls} w-full py-3`} type="password" placeholder="Current password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current-input" />
            <input className={`${inputCls} w-full py-3`} type="password" placeholder="New password (min 10 chars, upper, lower & number)" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new-input" />
            {error && <p className="text-sm text-red-600" data-testid="cp-error">{error}</p>}
            <button type="submit" disabled={busy} className="w-full bg-[#0B1B2B] text-white font-medium py-3.5 rounded-xl hover:bg-[#152a40] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="cp-submit-button">
              {busy && <Loader2 size={16} className="animate-spin" />}
              Update Password
            </button>
          </form>
        )}
      </div>
    </Modal>
  );
};

const inputCls =
  "px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors";

const ROLE_TITLES = {
  preceptor: "Preceptor",
  university: "University Coordinator",
  facility: "Facility Director",
  img: "Global USCE",
  admin: "Administrator",
};

const STATUS_OPTIONS = ["pending_review", "in_review", "approved", "declined"];
const statusCls = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  in_review: "bg-blue-50 text-blue-700 border-blue-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-red-50 text-red-600 border-red-200",
};

const PasswordGate = ({ onDone }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      onDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="password-gate">
      <div className="w-full max-w-md bg-white border border-[#D4AF37]/30 rounded-xl shadow-2xl p-8 md:p-10">
        <div className="w-12 h-12 rounded-xl bg-[#0B1B2B] flex items-center justify-center">
          <KeyRound size={22} className="text-[#D4AF37]" />
        </div>
        <h2 className="mt-5 font-serif text-2xl text-[#0B1B2B] tracking-tight">Set Your Permanent Password</h2>
        <p className="mt-2 text-sm text-[#64748B] leading-relaxed">
          For your security, please replace the temporary password issued with your access grant.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input className={`${inputCls} w-full py-3`} type="password" placeholder="Temporary password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="gate-current-password" />
          <input className={`${inputCls} w-full py-3`} type="password" placeholder="New password (min 10 chars, upper, lower & number)" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} data-testid="gate-new-password" />
          {error && <p className="text-sm text-red-600" data-testid="gate-error">{error}</p>}
          <button type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="gate-submit-button">
            {busy && <Loader2 size={16} className="animate-spin" />}
            Activate Account
          </button>
        </form>
      </div>
    </div>
  );
};

const StatMatrix = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 border border-[#E2E8F0] bg-white rounded-xl overflow-hidden shadow-xl">
    {stats.map((s, i) => (
      <div key={s.label} className={`p-6 md:p-8 border-[#E2E8F0] ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${i === 2 ? "lg:border-r" : ""}`}>
        <div className="font-serif text-3xl md:text-4xl text-[#0B1B2B]">{s.value}</div>
        <div className="mt-2 text-xs tracking-wide uppercase text-[#64748B]">{s.label}</div>
      </div>
    ))}
  </div>
);

const DataMatrix = ({ columns, rows }) => (
  <div className="mt-8 bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-[#0B1B2B]">
          {columns.map((c) => (
            <th key={c} className="text-left px-6 py-4 text-[#D4AF37] text-xs tracking-[0.15em] uppercase font-semibold">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
            <td className="px-6 py-4 font-medium text-[#0B1B2B]">{r.c1}</td>
            <td className="px-6 py-4 text-[#64748B]">{r.c2}</td>
            <td className="px-6 py-4 text-[#64748B]">{r.c3}</td>
            <td className="px-6 py-4 text-[#0B1B2B] font-medium">{r.c4}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VacancyAdmin = ({ vacancies, interests, refresh }) => {
  const empty = { title: "", specialty: "", setting: "", location: "", engagement: "" };
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const post = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/vacancies", form);
      setForm(empty);
      refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/vacancies/${id}`, { status });
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/vacancies/${id}`);
    refresh();
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-vacancies-panel">
      <form onSubmit={post} className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl p-6" data-testid="vacancy-post-form">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#64748B] mb-3">Post New Placement</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input className={inputCls} placeholder="Title" required value={form.title} onChange={set("title")} data-testid="vacancy-title-input" />
          <input className={inputCls} placeholder="Specialty" required value={form.specialty} onChange={set("specialty")} data-testid="vacancy-specialty-input" />
          <input className={inputCls} placeholder="Setting" required value={form.setting} onChange={set("setting")} data-testid="vacancy-setting-input" />
          <input className={inputCls} placeholder="Location" required value={form.location} onChange={set("location")} data-testid="vacancy-location-input" />
          <input className={inputCls} placeholder="Engagement" required value={form.engagement} onChange={set("engagement")} data-testid="vacancy-engagement-input" />
        </div>
        {error && <p className="mt-3 text-sm text-red-600" data-testid="vacancy-post-error">{error}</p>}
        <button type="submit" className="mt-4 bg-[#0B1B2B] text-white text-xs font-semibold px-5 py-2.5 rounded-lg hover:bg-[#152a40] transition-colors" data-testid="vacancy-post-button">
          Post Vacancy
        </button>
      </form>
      <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto" data-testid="admin-vacancy-list">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#0B1B2B]">{["Title", "Specialty", "Setting", "Location", "Engagement", "Status", ""].map((h, i) => <th key={i} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
          <tbody>
            {vacancies.map((v) => (
              <tr key={v.id} className="border-t border-[#E2E8F0]">
                <td className="px-5 py-3 font-medium text-[#0B1B2B]">{v.title}</td>
                <td className="px-5 py-3 text-[#64748B]">{v.specialty}</td>
                <td className="px-5 py-3 text-[#64748B]">{v.setting}</td>
                <td className="px-5 py-3 text-[#64748B]">{v.location}</td>
                <td className="px-5 py-3 text-[#64748B]">{v.engagement}</td>
                <td className="px-5 py-3">
                  <select value={v.status} onChange={(e) => setStatus(v.id, e.target.value)} className={`text-xs font-semibold px-2 py-1.5 rounded-lg border ${v.status === "open" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`vacancy-status-select-${v.id}`}>
                    <option value="open">open</option>
                    <option value="filled">filled</option>
                  </select>
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => remove(v.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Delete" data-testid={`vacancy-delete-${v.id}`}>
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {vacancies.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-[#64748B]">No vacancies posted.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto" data-testid="admin-interest-list">
        <div className="px-5 py-3 flex items-center justify-between border-b border-[#E2E8F0]">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Expressed Interest</span>
          <button onClick={() => downloadCsv("vacancy-interests", "acuity-rise-placement-interests.csv")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid="export-interests-csv">
            <FileDown size={13} className="text-[#D4AF37]" /> Export CSV
          </button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-[#0B1B2B]">{["Time", "Reference", "Vacancy", "Name", "Email", "Credentials", "Message"].map((h) => <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
          <tbody>
            {interests.map((i) => (
              <tr key={i.id} className="border-t border-[#E2E8F0]">
                <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">{new Date(i.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-[#0B1B2B] font-medium">{i.reference_id}</td>
                <td className="px-5 py-3 text-[#64748B]">{i.vacancy_title}</td>
                <td className="px-5 py-3 text-[#64748B]">{i.name}</td>
                <td className="px-5 py-3 text-[#64748B]">{i.email}</td>
                <td className="px-5 py-3 text-[#64748B]">{i.credentials || "—"}</td>
                <td className="px-5 py-3 text-[#64748B] text-xs max-w-[200px] truncate">{i.message || "—"}</td>
              </tr>
            ))}
            {interests.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-[#64748B]">No interest submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminQueue = ({ data, refresh }) => {
  const [tab, setTab] = useState("submissions");
  const [resched, setResched] = useState(null);
  const [reschedSlots, setReschedSlots] = useState([]);
  const [reschedError, setReschedError] = useState("");
  const [digestStatus, setDigestStatus] = useState("");
  const [screenState, setScreenState] = useState({});
  const [historyState, setHistoryState] = useState({});
  const [nursysOpen, setNursysOpen] = useState({});

  const toggleHistory = async (s) => {
    const cur = historyState[s.id];
    if (cur?.open) {
      setHistoryState((prev) => ({ ...prev, [s.id]: { ...cur, open: false } }));
      return;
    }
    setHistoryState((prev) => ({ ...prev, [s.id]: { open: true, busy: true, error: "", items: cur?.items || [] } }));
    try {
      const res = await api.get(`/submissions/${s.id}/screening-history`);
      setHistoryState((prev) => ({ ...prev, [s.id]: { open: true, busy: false, error: "", items: res.data.history } }));
    } catch (err) {
      setHistoryState((prev) => ({
        ...prev,
        [s.id]: { open: true, busy: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message, items: [] },
      }));
    }
  };

  const runScreen = async (s) => {
    setScreenState((prev) => ({ ...prev, [s.id]: { busy: true, error: "" } }));
    try {
      await api.post(`/submissions/${s.id}/verify`);
      setScreenState((prev) => ({ ...prev, [s.id]: { busy: false, error: "" } }));
      refresh();
    } catch (err) {
      setScreenState((prev) => ({
        ...prev,
        [s.id]: { busy: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message },
      }));
    }
  };

  const sendDigest = async () => {
    setDigestStatus("Sending…");
    try {
      await api.post("/admin/digest");
      setDigestStatus("Digest dispatched to admin & ops inboxes.");
    } catch {
      setDigestStatus("Digest dispatch failed.");
    }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/submissions/${id}/status`, { status });
    refresh();
  };

  const downloadFile = async (s) => {
    const res = await api.get(`/submissions/${s.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = s.file.original_name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cancelBooking = async (id) => {
    await api.patch(`/schedule/bookings/${id}`, { action: "cancel" });
    refresh();
  };

  const loadReschedSlots = async (date) => {
    setResched((r) => ({ ...r, date, slot: "" }));
    if (!date) return setReschedSlots([]);
    const { data: avail } = await api.get("/schedule/availability", { params: { date } });
    setReschedSlots(avail.slots.filter((s) => s.available).map((s) => s.time));
  };

  const confirmResched = async () => {
    setReschedError("");
    try {
      await api.patch(`/schedule/bookings/${resched.id}`, { action: "reschedule", date: resched.date, slot: resched.slot });
      setResched(null);
      refresh();
    } catch (err) {
      setReschedError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <div className="mt-10 flex flex-wrap items-center gap-1 border-b border-[#E2E8F0]">
        {[
          ["submissions", "Intake Queue", Inbox],
          ["bookings", "Consultations", CalendarDays],
          ["leads", "Vault Leads", Users],
          ["vacancies", "Vacancies", Briefcase],
          ["emails", "Dispatch Log", MailCheck],
          ["audit", "Audit Trail", ScrollText],
          ["forms", "Form Setup", SlidersHorizontal],
          ["verify", "Verification", BadgeCheck],
          ["engagement", "Engagement", Activity],
        ].map(([id, label, IconC]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors ${tab === id ? "text-[#0B1B2B] border-b-2 border-[#D4AF37]" : "text-[#64748B]"}`}
            data-testid={`admin-tab-${id}`}
          >
            <IconC size={15} /> {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 pb-1">
          {digestStatus && <span className="text-xs text-[#64748B]" data-testid="digest-status">{digestStatus}</span>}
          <button onClick={sendDigest} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid="send-digest-button">
            <MailCheck size={13} className="text-[#D4AF37]" /> Send Digest Now
          </button>
        </div>
      </div>
      {tab === "submissions" && (
        <div className="mt-6 space-y-4" data-testid="admin-submissions-list">
          {data.submissions.length === 0 && <p className="text-sm text-[#64748B] py-8">No intake submissions yet.</p>}
          {data.submissions.map((s) => (
            <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl p-6" data-testid={`submission-${s.reference_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-lg font-semibold text-[#0B1B2B]">{s.contact_name} <span className="text-[#64748B] font-sans text-sm font-normal">· {s.contact_email}</span>
                    {s.provisioned && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 align-middle" data-testid={`provision-badge-${s.reference_id}`}>
                        <BadgeCheck size={11} /> Portal Access Provisioned
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#64748B] mt-1">{s.track_label} · {s.reference_id} · {new Date(s.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      const res = await api.get(`/submissions/${s.id}/dossier`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `dossier-${s.reference_id}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors"
                    data-testid={`dossier-button-${s.reference_id}`}
                  >
                    <FileText size={13} className="text-[#D4AF37]" /> Dossier
                  </button>
                  {s.file && (
                    <button onClick={() => downloadFile(s)} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid={`download-file-${s.reference_id}`}>
                      <Download size={13} className="text-[#D4AF37]" /> Vault File
                    </button>
                  )}
                  <button
                    onClick={() => runScreen(s)}
                    disabled={screenState[s.id]?.busy}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors disabled:opacity-60"
                    data-testid={`screen-button-${s.reference_id}`}
                    title="Run NPI / OIG LEIE / SAM.gov screening — requires prior written candidate authorization"
                  >
                    {screenState[s.id]?.busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} className="text-[#D4AF37]" />}
                    {s.verification ? "Re-screen" : "Screen"}
                  </button>
                  <button
                    onClick={() => toggleHistory(s)}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors"
                    data-testid={`history-button-${s.reference_id}`}
                    title="View past screening runs for this preceptor"
                  >
                    <History size={13} className="text-[#D4AF37]" /> History
                  </button>
                  <button
                    onClick={async () => {
                      const res = await api.get(`/submissions/${s.id}/screening-report`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `screening-report-${s.reference_id}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors"
                    data-testid={`screening-report-button-${s.reference_id}`}
                    title="Download a PDF compliance report of every screening run"
                  >
                    <FileDown size={13} className="text-[#D4AF37]" /> Report
                  </button>
                  {s.track === "preceptors" && (
                    <button
                      onClick={() => setNursysOpen((p) => ({ ...p, [s.id]: !p[s.id] }))}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${s.nursys ? "border-emerald-300 text-emerald-700 hover:border-emerald-500" : "border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37]"}`}
                      data-testid={`nursys-button-${s.reference_id}`}
                      title="Enroll this preceptor in Nursys e-Notify license monitoring"
                    >
                      <BadgeCheck size={13} className={s.nursys ? "text-emerald-600" : "text-[#D4AF37]"} /> Nursys{s.nursys ? " ✓" : ""}
                    </button>
                  )}
                  <select
                    value={s.status}
                    onChange={(e) => updateStatus(s.id, e.target.value)}
                    className={`text-xs font-semibold px-3 py-2 rounded-lg border ${statusCls[s.status]}`}
                    data-testid={`submission-status-${s.reference_id}`}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o.replace("_", " ")}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                {Object.entries(s.data).slice(0, 9).map(([k, v]) => v && k !== "consent" && (
                  <div key={k} className="text-[#64748B]"><span className="text-[#0B1B2B] font-medium capitalize">{k.replace(/([A-Z])/g, " $1")}:</span> {String(v)}</div>
                ))}
                {s.file && (
                  <div className="text-[#64748B] md:col-span-3">
                    <span className="text-[#0B1B2B] font-medium">Vault file:</span> {s.file.original_name}
                    {s.file.metadata && Object.keys(s.file.metadata).length > 0 && (
                      <span className="ml-2 inline-flex flex-wrap gap-1.5 align-middle" data-testid={`file-metadata-${s.reference_id}`}>
                        {Object.entries(s.file.metadata).map(([k, v]) => {
                          let cls = "bg-[#0B1B2B] text-[#D4AF37]";
                          if (k === "expires_on") {
                            const today = new Date().toISOString().slice(0, 10);
                            const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
                            if (v < today) cls = "bg-red-600 text-white";
                            else if (v <= soon) cls = "bg-amber-500 text-white";
                          }
                          return (
                            <span key={k} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
                              {k.replace("_", " ")}: {v}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {screenState[s.id]?.error && (
                <p className="mt-3 text-sm text-red-600" data-testid={`screen-error-${s.reference_id}`}>{screenState[s.id].error}</p>
              )}
              {s.verification && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-[#0B1B2B] flex items-center gap-1.5">
                      <ShieldCheck size={13} className="text-[#D4AF37]" /> Compliance Screening
                    </p>
                    <span className="text-[10px] text-[#94A3B8]">
                      Run {new Date(s.verification.run_at).toLocaleString()} by {s.verification.run_by}
                    </span>
                  </div>
                  <p className={`text-xs mb-3 ${s.verification.review_required ? "text-[#8A6A12] font-semibold" : "text-[#166534]"}`}>
                    {s.verification.summary}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`screen-results-${s.reference_id}`}>
                    <ResultCard id="npi" result={s.verification.npi} />
                    <ResultCard id="oig_leie" result={s.verification.oig_leie} />
                    <ResultCard id="sam_exclusions" result={s.verification.sam_exclusions} />
                  </div>
                </div>
              )}
              {historyState[s.id]?.open && (
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-testid={`history-panel-${s.reference_id}`}>
                  <p className="text-xs font-semibold text-[#0B1B2B] flex items-center gap-1.5 mb-2">
                    <History size={13} className="text-[#D4AF37]" /> Screening History
                  </p>
                  {historyState[s.id].busy && <p className="text-xs text-[#64748B]">Loading past runs…</p>}
                  {historyState[s.id].error && <p className="text-xs text-red-600">{historyState[s.id].error}</p>}
                  {!historyState[s.id].busy && !historyState[s.id].error && historyState[s.id].items.length === 0 && (
                    <p className="text-xs text-[#94A3B8]">No screening runs recorded yet for this preceptor.</p>
                  )}
                  {!historyState[s.id].busy && historyState[s.id].items.length > 0 && (
                    <div className="space-y-1.5">
                      {historyState[s.id].items.map((h, i) => (
                        <div
                          key={i}
                          className={`flex flex-wrap items-center gap-2 text-xs rounded-lg px-3 py-2 border ${h.review_required ? "border-[#D4AF37] bg-[#FDF8ED]" : "border-[#E2E8F0]"}`}
                        >
                          <span className="font-medium text-[#0B1B2B]">{new Date(h.run_at).toLocaleString()}</span>
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#0B1B2B] text-[#D4AF37]">
                            {h.trigger === "scheduled" ? "Automated (monthly)" : "Manual"}
                          </span>
                          <span className={h.review_required ? "text-[#8A6A12] font-semibold" : "text-[#166534]"}>
                            {h.review_required ? "Flagged for review" : "Clean"}
                          </span>
                          <span className="text-[#64748B]">
                            OIG: {h.oig_matches ?? 0} · SAM: {h.sam_matches ?? 0}
                            {h.npi_matches != null ? ` · NPI: ${h.npi_matches}` : ""}
                          </span>
                          <span className="text-[#94A3B8]">by {h.run_by}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {nursysOpen[s.id] && <NursysEnrollPanel s={s} refresh={refresh} />}
            </div>
          ))}
        </div>
      )}
      {tab === "bookings" && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto" data-testid="admin-bookings-list">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0B1B2B]">{["Date", "Slot", "Name", "Email", "Status", "Confirmation", "Actions"].map((h) => <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {data.bookings.map((b) => (
                <Fragment key={b.id}>
                  <tr className="border-t border-[#E2E8F0]">
                    <td className="px-5 py-3 font-medium text-[#0B1B2B]">{b.date}</td>
                    <td className="px-5 py-3 text-[#64748B]">{b.slot}</td>
                    <td className="px-5 py-3 text-[#64748B]">{b.name}</td>
                    <td className="px-5 py-3 text-[#64748B]">{b.email}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${b.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{b.status}</span>
                    </td>
                    <td className="px-5 py-3 text-[#0B1B2B] font-medium">{b.confirmation_id}</td>
                    <td className="px-5 py-3">
                      {b.status === "confirmed" && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setResched({ id: b.id, date: "", slot: "" }); setReschedSlots([]); setReschedError(""); }} className="p-1.5 rounded-lg text-[#64748B] hover:text-[#0B1B2B] hover:bg-[#F8FAFC]" title="Reschedule" data-testid={`reschedule-booking-${b.confirmation_id}`}>
                            <CalendarClock size={16} />
                          </button>
                          <button onClick={() => cancelBooking(b.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" title="Cancel" data-testid={`cancel-booking-${b.confirmation_id}`}>
                            <XCircle size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {resched?.id === b.id && (
                    <tr className="border-t border-[#E2E8F0] bg-[#F8FAFC]">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Reschedule to:</span>
                          <input type="date" className={inputCls} value={resched.date} onChange={(e) => loadReschedSlots(e.target.value)} data-testid="resched-date-input" />
                          <select className={inputCls} value={resched.slot} onChange={(e) => setResched({ ...resched, slot: e.target.value })} data-testid="resched-slot-select">
                            <option value="">Select slot…</option>
                            {reschedSlots.map((t) => <option key={t} value={t}>{t} ET</option>)}
                          </select>
                          <button onClick={confirmResched} disabled={!resched.date || !resched.slot} className="bg-[#0B1B2B] text-white text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50" data-testid="resched-confirm-button">Confirm</button>
                          <button onClick={() => setResched(null)} className="text-xs text-[#64748B]">Dismiss</button>
                          {reschedError && <span className="text-xs text-red-600" data-testid="resched-error">{reschedError}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {data.bookings.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-[#64748B]">No consultations booked yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab === "leads" && (
        <div className="mt-6" data-testid="admin-leads-list">
          <div className="flex items-center justify-end gap-2 mb-3">
            <button onClick={() => downloadCsv("resource-leads", "acuity-rise-vault-leads.csv")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid="export-leads-csv">
              <FileDown size={13} className="text-[#D4AF37]" /> Export Leads CSV
            </button>
            <button onClick={() => downloadCsv("demo-requests", "acuity-rise-demo-requests.csv")} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid="export-demo-csv">
              <FileDown size={13} className="text-[#D4AF37]" /> Export Demo Requests CSV
            </button>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0B1B2B]">{["Time", "Name", "Organization", "Email", "Whitepapers Downloaded"].map((h) => <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {(data.resource_leads || []).map((l) => (
                <tr key={l.id} className="border-t border-[#E2E8F0]">
                  <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3 font-medium text-[#0B1B2B]">{l.name}</td>
                  <td className="px-5 py-3 text-[#64748B]">{l.organization}</td>
                  <td className="px-5 py-3 text-[#64748B]">{l.email}</td>
                  <td className="px-5 py-3">
                    {(l.downloads || []).length === 0 ? (
                      <span className="text-xs text-[#94A3B8]">Unlocked — no downloads yet</span>
                    ) : (
                      <span className="inline-flex flex-wrap gap-1.5">
                        {l.downloads.map((d) => (
                          <span key={d} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#0B1B2B] text-[#D4AF37]">{d}</span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(!data.resource_leads || data.resource_leads.length === 0) && <tr><td colSpan={5} className="px-5 py-8 text-[#64748B]">No vault leads captured yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {tab === "vacancies" && <VacancyAdmin vacancies={data.vacancies || []} interests={data.vacancy_interests || []} refresh={refresh} />}
      {tab === "emails" && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto" data-testid="admin-emails-list">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0B1B2B]">{["Time", "Kind", "To", "Subject", "Status"].map((h) => <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {data.email_events.map((e) => (
                <tr key={e.id} className="border-t border-[#E2E8F0]">
                  <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3 text-[#64748B]">{e.kind}</td>
                  <td className="px-5 py-3 text-[#64748B]">{e.to.join(", ")}</td>
                  <td className="px-5 py-3 text-[#0B1B2B]">{e.subject}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded ${e.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{e.status}</span></td>
                </tr>
              ))}
              {data.email_events.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-[#64748B]">No dispatch events yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab === "audit" && (
        <>
          <div className="mt-6 flex justify-end">
            <button
              onClick={async () => {
                const res = await api.get("/audit/export", { responseType: "blob" });
                const cd = res.headers["content-disposition"] || "";
                const match = cd.match(/filename="?([^";]+)"?/);
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = match ? match[1] : "acuity-audit-trail.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 bg-[#0B1B2B] text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:bg-[#152a40] transition-colors"
              data-testid="audit-export-button"
            >
              <FileDown size={14} className="text-[#D4AF37]" /> Export CSV
            </button>
          </div>
          <div className="mt-3 bg-white border border-[#E2E8F0] rounded-xl shadow-xl overflow-x-auto" data-testid="admin-audit-list">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#0B1B2B]">{["Time", "Action", "Actor", "IP", "Detail"].map((h) => <th key={h} className="text-left px-5 py-3 text-[#D4AF37] text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {(data.audit_log || []).map((a) => (
                <tr key={a.id} className="border-t border-[#E2E8F0]">
                  <td className="px-5 py-3 text-[#64748B] whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3"><span className="text-xs font-semibold px-2 py-1 rounded bg-[#F8FAFC] border border-[#E2E8F0] text-[#0B1B2B]">{a.action}</span></td>
                  <td className="px-5 py-3 text-[#64748B]">{a.actor}</td>
                  <td className="px-5 py-3 text-[#64748B] text-xs">{a.ip || "—"}</td>
                  <td className="px-5 py-3 text-[#64748B] text-xs">{Object.entries(a.meta || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</td>
                </tr>
              ))}
              {(!data.audit_log || data.audit_log.length === 0) && <tr><td colSpan={5} className="px-5 py-8 text-[#64748B]">No audit entries yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </>
      )}
      {tab === "forms" && <FormSetup />}
      {tab === "verify" && <ComplianceVerification />}
      {tab === "engagement" && <BucketEngagement />}
    </>
  );
};

const SubscriberPortal = ({ role, data, userEmail }) => {
  const [tab, setTab] = useState("console");
  const [intake, setIntake] = useState({ open: false, trackId: null, specialty: null, values: null });
  const [tourStep, setTourStep] = useState(() => (localStorage.getItem(`ar_tour_${userEmail}`) ? -1 : 0));
  const beginIntake = (trackId, specialty = null, values = null) => setIntake({ open: true, trackId, specialty, values });
  const tracks = TRACKS.filter((t) => (ROLE_TRACKS[role] || []).includes(t.id));

  const endTour = () => {
    localStorage.setItem(`ar_tour_${userEmail}`, "1");
    setTourStep(-1);
    setTab("console");
  };
  const tourNext = () => {
    if (tourStep >= TOUR_STEPS.length - 1) return endTour();
    setTab(TOUR_STEPS[tourStep + 1].id);
    setTourStep(tourStep + 1);
  };
  const tourBack = () => {
    setTab(TOUR_STEPS[tourStep - 1].id);
    setTourStep(tourStep - 1);
  };

  return (
    <>
      <div className="mt-10 flex flex-wrap items-center gap-1 border-b border-[#E2E8F0]">
        {[
          ["console", "Operations Console", LayoutDashboard],
          ["pathways", "Operational Pathways", Map],
          ["scheduler", "Executive Scheduler", CalendarClock],
          ["vault", "Resource Vault", FolderLock],
          ["assessment", "Self-Assessment", ClipboardCheck],
          ["placements", "Placements", Briefcase],
        ].map(([id, label, IconC]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === id ? "text-[#0B1B2B] border-b-2 border-[#D4AF37]" : "text-[#64748B]"}`}
            data-testid={`portal-tab-${id}`}
          >
            <IconC size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === "console" && (
        <div data-testid="portal-console">
          <div className="mt-8">
            <StatMatrix stats={data.stats} />
          </div>
          <MonitoringWidgets />
          <DataMatrix columns={data.columns} rows={data.rows} />
        </div>
      )}
      {tab === "pathways" && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="portal-pathways">
          <Pathways gated tracks={tracks} onBeginIntake={beginIntake} />
          {role === "preceptor" && <AutonomousSuites />}
        </div>
      )}
      {tab === "scheduler" && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="portal-scheduler">
          <Scheduler />
        </div>
      )}
      {tab === "vault" && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="portal-vault">
          <ResourceVault />
        </div>
      )}
      {tab === "assessment" && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="portal-assessment">
          <ComplianceAudit onBeginIntake={beginIntake} />
        </div>
      )}
      {tab === "placements" && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="portal-placements">
          <VacancyBoard />
        </div>
      )}
      {tourStep >= 0 && <PortalTour step={tourStep} onNext={tourNext} onBack={tourBack} onSkip={endTour} />}
      <IntakeModal
        open={intake.open}
        trackId={intake.trackId}
        presetSpecialty={intake.specialty}
        presetValues={intake.values}
        onClose={() => setIntake({ ...intake, open: false })}
      />
    </>
  );
};

export default function Dashboard() {
  const { user, loading, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  const role = user?.role;

  const fetchData = () => {
    if (!role) return;
    api
      .get(`/dashboard/${role}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load dashboard"));
  };

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(fetchData, [role]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );

  if (user?.must_change_password)
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <header className="bg-[#0B1B2B] border-b border-[#D4AF37]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[76px] flex items-center gap-3">
            <BrandEmblem />
            <div className="font-serif text-lg text-white font-semibold">Acuity Rise Portal</div>
          </div>
        </header>
        <PasswordGate onDone={updateUser} />
      </div>
    );

  if (!data && !error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
      <header className="bg-[#0B1B2B] border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[76px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandEmblem />
            <div>
              <div className="font-serif text-lg text-white font-semibold leading-tight">Acuity Rise Portal</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">{ROLE_TITLES[role] || role} Console</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setPwOpen(true)} className="text-sm text-white/80 hover:text-white flex items-center gap-1.5 transition-colors" data-testid="change-password-button">
              <KeyRound size={15} className="text-[#D4AF37]" /> <span className="hidden sm:inline">Change Password</span>
            </button>
            <button onClick={() => navigate("/")} className="text-sm text-white/80 hover:text-white flex items-center gap-1.5 transition-colors" data-testid="dashboard-back-button">
              <ArrowLeft size={15} /> <span className="hidden sm:inline">Public Site</span>
            </button>
            <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-[#D4AF37] font-semibold flex items-center gap-1.5" data-testid="dashboard-logout-button">
              <LogOut size={15} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {error ? (
          <p className="text-red-600" data-testid="dashboard-error">{String(error)}</p>
        ) : (
          <>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Simulated Operational View</p>
            <h1 className="mt-3 font-serif text-3xl md:text-4xl text-[#0B1B2B] tracking-tight" data-testid="dashboard-title">{data.title}</h1>
            <p className="mt-2 text-[#64748B]">Welcome back, {user.name}{user.organization ? ` — ${user.organization}` : ""}.</p>
            {role === "admin" ? (
              <>
                <div className="mt-10">
                  <StatMatrix stats={data.stats} />
                </div>
                <AdminQueue data={data} refresh={fetchData} />
              </>
            ) : (
              <SubscriberPortal role={role} data={data} userEmail={user.email} />
            )}
          </>
        )}
      </main>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
